import {
	SortableContext,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useId, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";
import { useAdminFormEditorPaths } from "../../hooks/use-admin-form-editor-paths";
import { Plus } from "../../lib/palette-metadata";
import { useFormEditorStore } from "../../state/form-editor-store";
import {
	type FormBuilderCanvasContextValue,
	FormBuilderCanvasProvider,
} from "./form-builder-canvas-context";
import { FORM_BUILDER_ADD_STRIP_BASE } from "./form-builder-layout-classes";
import { FormBuilderRootDropArea } from "./form-builder-root-drop-area";
import { BUILDER_CONTROL_FOCUS } from "./form-builder-shared-classes";
import { FormBuilderRootItemRow } from "./rows/form-builder-root-item-row";

/**
 * Left column: builder title, root drop zone, sortable root list, and add-section control.
 * Subscribes to canvas-related store slices and supplies FormBuilderCanvasProvider for rows.
 */
export function FormBuilderCanvas() {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();
	const canvasHeadingId = useId();

	const canvas = useFormEditorStore(
		useShallow((s) => ({
			rootIds: s.rootIds,
			nodes: s.nodes,
			selectedId: s.selectedId,
			setSelectedId: s.setSelectedId,
			moveSection: s.moveSection,
			addSection: s.addSection,
			clearSectionFields: s.clearSectionFields,
			deleteNode: s.deleteNode,
			moveField: s.moveField,
		})),
	);

	const canvasContextValue = useMemo((): FormBuilderCanvasContextValue => {
		return {
			nodes: canvas.nodes,
			rootIds: canvas.rootIds,
			clearSectionFields: canvas.clearSectionFields,
			deleteNode: canvas.deleteNode,
			moveField: canvas.moveField,
		};
	}, [
		canvas.nodes,
		canvas.rootIds,
		canvas.clearSectionFields,
		canvas.deleteNode,
		canvas.moveField,
	]);

	const rootEmpty = canvas.rootIds.length === 0;

	return (
		<FormBuilderCanvasProvider value={canvasContextValue}>
			<section
				className="flex min-w-0 flex-1 flex-col border-r border-border-secondary bg-background-primary"
				aria-labelledby={canvasHeadingId}
			>
				<header className="border-b border-border-secondary px-6 py-4">
					<h2
						id={canvasHeadingId}
						className="text-lg font-semibold text-text-primary"
					>
						{t(paths.formEditor.builder.title)}
					</h2>
					<p className="text-sm text-text-tertiary">
						{t(paths.formEditor.builder.subtitle)}
					</p>
				</header>

				<FormBuilderRootDropArea
					aria-label={t(paths.formEditor.a11y.formCanvasDropRegion)}
				>
					<div className="p-6">
						{rootEmpty ? (
							<div className="py-12 text-center text-fg-quaternary">
								<p>{t(paths.formEditor.builder.emptyTitle)}</p>
								<p className="mt-1 text-sm text-text-tertiary">
									{t(paths.formEditor.builder.emptySubtitle)}
								</p>
							</div>
						) : (
							<SortableContext
								items={canvas.rootIds}
								strategy={verticalListSortingStrategy}
							>
								<ul
									className="space-y-2"
									aria-label={t(paths.formEditor.a11y.formStructureList)}
								>
									{canvas.rootIds.map((id) => (
										<FormBuilderRootItemRow
											key={id}
											id={id}
											node={canvas.nodes[id]}
											selectedId={canvas.selectedId}
											onSelect={canvas.setSelectedId}
											onMoveSection={canvas.moveSection}
										/>
									))}
								</ul>
							</SortableContext>
						)}

						<button
							type="button"
							data-testid="form-builder-add-section"
							onClick={() => {
								canvas.addSection(t(paths.formEditor.defaults.newSectionLabel));
							}}
							className={cx(
								"mt-4",
								FORM_BUILDER_ADD_STRIP_BASE,
								BUILDER_CONTROL_FOCUS,
								"hover:border-brand-500 hover:bg-brand-50",
							)}
						>
							<Plus className="size-4" aria-hidden />
							{t(paths.formEditor.builder.addSection)}
						</button>
					</div>
				</FormBuilderRootDropArea>
			</section>
		</FormBuilderCanvasProvider>
	);
}
